'use client';

import { Switch as DSSwitch } from '@lulalife/design-system';
import get from 'lodash/get';
import { useController } from 'react-hook-form';

interface SwitchProps {
  name: string;
  label?: string;
  hideFormError?: boolean;
  className?: string;
  checked?: boolean;
  value?: unknown;
  handleClick?: ((e: React.ChangeEvent<HTMLInputElement>) => void) | null;
  disabled?: boolean;
  dataTestId?: string;
}

/**
 * Same `name`-bound, `useController`-wired API as
 * `src/components/Shared/FormFields/Toggle.tsx` — only the rendered control
 * changed, from a reactstrap `FormGroup switch`/`Input type="switch"` to the
 * design system's `Switch`. A call site swaps the import and keeps every prop
 * it already passes.
 *
 * `checked` keeps the same override behaviour the old `Toggle` had: pass it
 * when the field's own stored value isn't already the boolean the switch needs
 * (e.g. `account.account_status` stores `'active'`/`'inactive'`, so the caller
 * computes `checked={accountStatus === 'active'}` itself, same as before).
 *
 * Deliberately not carried over: `formGroupClass` (a reactstrap `FormGroup`
 * concern the DS `Switch` has no equivalent slot for).
 */
export const Toggle = (props: SwitchProps) => {
  // `value` accepted for prop parity with the old `Toggle`, but a native
  // checkbox-backed `Switch` is bound by `checked`, not `value` — see the
  // docblock above.
  const { label, name, hideFormError, checked, handleClick, className, dataTestId } = props;

  const {
    field,
    formState: { errors },
  } = useController({ name });

  const fieldError = get(errors, name);
  const fieldErrorMessage =
    typeof fieldError?.message === 'string' ? fieldError.message : undefined;

  return (
    <>
      <DSSwitch
        id={name}
        className={className}
        label={label}
        checked={checked}
        data-testid={dataTestId}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          if (handleClick) {
            field.onChange(handleClick(e));
          } else {
            field.onChange(e.target.checked);
          }
        }}
        onBlur={field.onBlur}
        disabled={props.disabled}
      />
      {!hideFormError && fieldErrorMessage ? (
        <span className="body-xs text-text-danger">{fieldErrorMessage}</span>
      ) : null}
    </>
  );
};

export default Toggle;
